import { findBannerAncestor } from './banners';
import { getRoot, getSelector } from './dom';

/** Тег-обёртка, в которую @cian/mf-registry заворачивает разметку микрофронтенда. */
const MICROFRONTEND_TAG = 'cian-mf-ui';

const HEADER_MICROFRONTEND_NAME = 'header-frontend';
const FOOTER_MICROFRONTEND_NAME = 'frontend-footer';

export interface ILayoutShiftSource {
  node?: Node | null;
  previousRect?: DOMRectReadOnly;
  currentRect?: DOMRectReadOnly;
}

export interface ILayoutShift extends PerformanceEntry {
  value: number;
  hadRecentInput: boolean;
  sources?: ILayoutShiftSource[];
}

export interface IOwnershipSplit {
  /** Двигались потомки корня — перестроилась наша разметка. */
  ours: number;
  /**
   * Источником числится сам корень приложения. Он себя не двигает: значит его
   * толкнуло то, что выше — шапка, баннер, любой внешний блок. Мы тут пострадавший.
   */
  rootMoved: number;
  /**
   * Сдвиг внутри рекламного блока. Отдельно от шапки и футера, хотя физически
   * баннер лежит внутри них: грузится он отдельно и чинится не там, где вёрстка.
   */
  banner: number;
  /** Сдвиг внутри микрофронтенда шапки. */
  header: number;
  /** Сдвиг внутри микрофронтенда футера. */
  footer: number;
  /** Всё остальное вне корня: сторонние виджеты, чужие микрофронтенды. */
  foreign: number;
  /** Сдвиг без источников: браузер их не отдал. */
  unknown: number;
  /** scrollY в момент самого крупного сдвига — не итоговый, а именно тогда. */
  scrollYAtWorst: number;
  worstValue: number;
  /** Источники крупнейшего сдвига: по ним видно, кто менял высоту, а кого просто сдвинуло. */
  worstSources: string[];
  /**
   * Кто менял высоту за всю сессию, а не только в крупнейшем сдвиге.
   * `dh != 0` — признак виновника: он раздвинул вёрстку, остальных просто отнесло.
   */
  heightChangers: string[];
  /** Высота шапки до и после гидратации: если различаются, причина снаружи нашего репозитория. */
  headerHeightStart: number | null;
  headerHeightEnd: number | null;
  /**
   * Высота шапки в момент крупнейшего сдвига. Без неё схлопывание не поймать:
   * к моменту отчёта шапка успевает вернуться к исходной высоте, и пара
   * Start/End показывает одно и то же число, хотя вёрстку уже разорвало.
   */
  headerHeightAtWorst: number | null;
}

export type TOwner = 'ours' | 'rootMoved' | 'banner' | 'header' | 'footer' | 'foreign';

function getMicrofrontend(name: string): Element | null {
  return document.querySelector(`${MICROFRONTEND_TAG}[name="${name}"]`);
}

/**
 * Высота шапки. Меряем сам микрофронтенд, а если его в разметке нет —
 * падаем на расстояние от верха документа до корня приложения: это то же самое место,
 * просто измеренное снаружи.
 */
export function getHeaderHeight(rootId: string): number | null {
  const header = getMicrofrontend(HEADER_MICROFRONTEND_NAME);

  if (header) {
    return Math.round(header.getBoundingClientRect().height);
  }

  const root = getRoot(rootId);

  return root ? Math.round(root.getBoundingClientRect().top + window.scrollY) : null;
}

/** Высота нашего корня: выросла наша часть после гидратации или сжалась. */
export function getRootHeight(rootId: string): number | null {
  const root = getRoot(rootId);

  return root ? Math.round(root.getBoundingClientRect().height) : null;
}

export { getSelector };

const signed = (value: number) => (value > 0 ? `+${value}` : String(value));

/** Источники, по которым браузер отдал живой элемент: остальные в логе бесполезны. */
function withNodes(sources: ILayoutShiftSource[]): ILayoutShiftSource[] {
  return sources.filter(source => source.node?.nodeType === Node.ELEMENT_NODE);
}

/** Селекторы тех, у кого изменилась высота, — именно они раздвигают вёрстку. */
export function collectHeightChangers(sources: ILayoutShiftSource[]): string[] {
  return withNodes(sources)
    .filter(({ previousRect: before, currentRect: after }) => before && after && before.height !== after.height)
    .map(source => getSelector(source.node));
}

/**
 * Источник в одну строку: `селектор dy=… dh=… dx=… dw=…`.
 *
 * Все четыре величины обязательны: элементы шапки умеют меняться по горизонтали,
 * и версия только с `dy`/`dh` показывала бы у них нули при реальном сдвиге.
 *
 * Логгер не принимает вложенные объекты в мета, да и в Discover строки читаются удобнее.
 */
export function serializeSources(sources: ILayoutShiftSource[]): string[] {
  return withNodes(sources)
    .slice(0, 5)
    .map(source => {
      const selector = getSelector(source.node);
      const before = source.previousRect;
      const after = source.currentRect;

      if (!before || !after) {
        return `${selector} dy=? dh=? dx=? dw=?`;
      }

      const dy = signed(Math.round(after.y - before.y));
      const dh = signed(Math.round(after.height - before.height));
      const dx = signed(Math.round(after.x - before.x));
      const dw = signed(Math.round(after.width - before.width));

      return `${selector} dy=${dy} dh=${dh} dx=${dx} dw=${dw}`;
    });
}

interface INetworkInformation {
  effectiveType?: string;
  rtt?: number;
  downlink?: number;
  saveData?: boolean;
}

export interface ISessionInfo {
  /** 4g / 3g / 2g / slow-2g — оценка канала самим браузером. */
  effectiveType: string | null;
  /** Оценка круговой задержки, мс. */
  rtt: number | null;
  /** Гигабайты памяти и число ядер: отличает слабое железо от быстрого. */
  deviceMemory: number | null;
  hardwareConcurrency: number | null;
}

/** Характеристики сессии. Ничего персонального: класс канала и класс железа. */
export function getSessionInfo(): ISessionInfo {
  const nav = navigator as Navigator & { connection?: INetworkInformation; deviceMemory?: number };
  const conn = nav.connection;

  return {
    effectiveType: conn?.effectiveType ?? null,
    rtt: typeof conn?.rtt === 'number' ? conn.rtt : null,
    deviceMemory: typeof nav.deviceMemory === 'number' ? nav.deviceMemory : null,
    hardwareConcurrency: typeof navigator.hardwareConcurrency === 'number' ? navigator.hardwareConcurrency : null,
  };
}

/**
 * CLS показывает пострадавший элемент, а не виновника. Поэтому различаем случаи:
 * сам корень уехал (виноват кто-то снаружи), двигался рекламный блок,
 * уехали потомки корня (виноваты мы), уехало внутри шапки или футера
 * (виноват соответствующий микрофронтенд), уехало что-то ещё вне корня.
 *
 * Реклама проверяется раньше микрофронтендов: физически баннер лежит внутри
 * футера, но грузится отдельно, и чинить его надо не там, где вёрстку футера.
 */
export function classifySource(node: Node | null | undefined, rootId: string): TOwner {
  if (!node || node.nodeType !== Node.ELEMENT_NODE) {
    return 'foreign';
  }

  const element = node as Element;

  if (element.id === rootId) {
    return 'rootMoved';
  }

  if (findBannerAncestor(element)) {
    return 'banner';
  }

  if (element.closest(`#${rootId}`)) {
    return 'ours';
  }

  const microfrontendName = element.closest(MICROFRONTEND_TAG)?.getAttribute('name');

  if (microfrontendName === HEADER_MICROFRONTEND_NAME) {
    return 'header';
  }

  if (microfrontendName === FOOTER_MICROFRONTEND_NAME) {
    return 'footer';
  }

  return 'foreign';
}

/**
 * Готовый ответ на «кто виноват», одной строкой.
 * Логгер раскладывает мету в коллекцию пар key/value, поэтому в ELK удобнее
 * фильтровать по заранее посчитанной категории, чем собирать её запросом.
 */
export function getCulprit(split: IOwnershipSplit): string {
  const { headerHeightStart: start, headerHeightEnd: end } = split;

  if (start !== null && end !== null && start !== end) {
    return 'header';
  }

  const buckets: [string, number][] = [
    ['ours', split.ours],
    ['outside', split.rootMoved],
    ['banner', split.banner],
    ['header', split.header],
    ['footer', split.footer],
    ['foreign', split.foreign],
    ['unknown', split.unknown],
  ];

  const [name, value] = buckets.reduce((best, current) => (current[1] > best[1] ? current : best));

  return value === 0 ? 'none' : name;
}

/** Как пользователь попал на страницу — от этого зависит, где стоял экран к моменту гидратации. */
export function getEntryType(navType: string | null): string {
  if (window.location.hash) {
    return 'anchor';
  }

  return navType === 'back_forward' || navType === 'reload' ? 'restored' : 'plain';
}
